import {
  getMessageStyle,
  regionAssetApi,
  regionInstanceApi,
  regionUserHelperApi,
  useAppDispatch,
  useAppSelector,
  useGetProvisionStatusQuery,
  useLazyGetUserOrgNodesQuery,
  useProvisionInstanceMutation
} from '@monorepo/shared';
import {
  CountDown,
  ImageVersionSelector,
  ProvisionActions,
  ProvisionConfirm,
  RegionSelector
} from '@monorepo/shared/components';
import { useState, useEffect } from 'react';
import { Col, Form, Grid, Message, Row, Stack } from 'rsuite';
import { server, HomeElms } from '../../config/const';
import { useTranslation } from 'react-i18next';
import { selectRejectedRegions } from '@monorepo/shared/store/authSlice';
import serviceTermPdf from '/FortiSandbox-Cloud-Service-Description.pdf';

interface ProvisionIProps {
  rowData: Record<string, any>;
  existedTaskObj?: Record<string, any>;
}

const ProvisionService = ({ rowData, existedTaskObj }: ProvisionIProps) => {
  const { t } = useTranslation();
  const initInfo: Record<string, any> = {
    showIcon: true,
    type: 'info',
    text: <span>{t('common.provision.confirmInfoWithRegion.part1')}</span>
  };
  const rejectedRegions = useAppSelector(selectRejectedRegions);
  const regionAlias = rowData?.region?.alias;
  const [message, setMessage] = useState<Record<string, any>>(initInfo);
  const [provisionInstance, getTaskIdResult] = useProvisionInstanceMutation();
  const [taskObj, setTaskObj] = useState<Record<string, any>>(
    existedTaskObj ? existedTaskObj : {}
  );
  const [getAccounts] = useLazyGetUserOrgNodesQuery();
  const [imageVersion, setImageVersion] = useState<string>('');
  const [adomVersion, setAdomVersion] = useState<string>('');
  const [resetNum, setResetNum] = useState<number>(0);
  const [timezone, setTimezone] = useState<Record<string, any>>({
    label: '',
    rawValue: ''
  });
  const [region, setRegion] = useState<Record<string, any>>({
    rawValue: regionAlias,
    label: regionAlias
  });
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const {
    data: provisionStatus,
    error: provisionError,
    isSuccess: statusFilled
  } = useGetProvisionStatusQuery(
    {
      region: region?.rawValue,
      taskId: taskObj?.task_id,
      accountIds: [taskObj?.account_id || rowData?.account_id]
    },
    { skip: !taskObj?.task_id, pollingInterval: 5000 }
  );
  const getRequiredProps = () => {
    return {
      region: {
        value: region?.rawValue,
        errorMessage: `${t('common.provision.regionRequire')}`
      }
    };
  };
  const refetch = async () => {
    await getAccounts({
      server: server,
      fcName: HomeElms?.fcName,
      refresh: true
    });
  };
  useEffect(() => {
    if (getTaskIdResult.error) {
      const data: Record<string, any> = getTaskIdResult.error;
      if (data?.data?.error_desc && setMessage) {
        setMessage({
          type: 'error',
          showIcon: true,
          text: `${data?.data?.error_desc} [Error_code: ${data?.data?.error_code}]`
        });
      }
    } else if (getTaskIdResult.data) {
      const data: Record<string, any> = getTaskIdResult.data;
      if (data?.task_id) {
        setTaskObj(data);
      } else if (data?.login_url) {
        console.warn('This instance already has url exists', data?.login_url);
        refetch();
      } else if (data?.error_desc && setMessage) {
        setMessage({
          type: 'error',
          showIcon: true,
          text: `${data?.error_desc} [Error_code: ${data?.error_code}]`
        });
      }
    }
  }, [getTaskIdResult]);
  useEffect(() => {
    const rejectedRegionNames = Object.keys(rejectedRegions);
    if (
      setMessage &&
      rejectedRegionNames.length &&
      rejectedRegionNames.includes(region?.rawValue)
    ) {
      setMessage({
        type: 'error',
        showIcon: true,
        text: t('common.provision.regionNotAvailable', {
          region: region?.label
        })
      });
    } else {
      setMessage && !provisionStatus && setMessage(initInfo);
    }
  }, [region, rejectedRegions, t]);
  useEffect(() => {
    if (provisionStatus) {
      if (provisionStatus.status === 'FAILURE' || provisionStatus.error_desc) {
        setTaskObj({});
        if (setMessage) {
          setMessage({
            type: 'error',
            showIcon: true,
            text: `${provisionStatus.error_desc} [Error_code: ${provisionStatus.error_code}]`
          });
        }
      } else if (
        provisionStatus?.stage ||
        provisionStatus.status === 'PENDING'
      ) {
        const stage = provisionStatus?.stage;
        const msg = provisionStatus.msg;
        if (stage === 'DONE') {
          refetch();
        } else if (setMessage) {
          setMessage({
            type: 'success',
            showIcon: false,
            content: <CountDown stage={stage} msg={msg} refreshPage={refetch} />
          });
        }
      }
    }
    if (provisionError && setMessage) {
      const error: Record<string, any> = provisionError;
      setMessage({
        type: 'error',
        showIcon: true,
        text: `${t('common.provision.provisionFail')} ${
          error?.data?.detail
        } [Error_code: ${error?.status}]`
      });
      setTaskObj({});
    }
  }, [provisionStatus, provisionError, t]);
  return (
    <Grid fluid className="instance-content">
      <Row className="show-grid instance-content-row" gutter={0}>
        <Col xs={24} className="instance-content-col">
          <div
            className="instance-content-container"
            style={{
              marginRight: '10px'
            }}
          >
            <Stack
              className="instance-info"
              direction="column"
              alignItems="stretch"
            >
              <Stack direction="column" alignItems="stretch" spacing={10}>
                <Stack justifyContent="space-between">
                  <b style={{ fontSize: '16px' }}>
                    {t('common.provision.provisionTitle')}
                  </b>
                </Stack>
                <Message
                  showIcon={message.showIcon}
                  type={message.type}
                  style={{
                    ...getMessageStyle(message.type),
                    marginBottom: '8px'
                  }}
                >
                  <span style={{ color: getMessageStyle(message.type).color }}>
                    {message.content || message.text}
                  </span>
                </Message>
                {existedTaskObj?.task_id ? (
                  <></>
                ) : (
                  <ProvisionForm
                    region={region}
                    timezone={timezone}
                    setAdomVersion={setAdomVersion}
                    imageVersion={imageVersion}
                    setImageVersion={setImageVersion}
                    setTimezone={setTimezone}
                    setRegion={setRegion}
                    resetNum={resetNum}
                  />
                )}
              </Stack>
            </Stack>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={24} className="instance-content-controller">
          <ProvisionActions
            requiredProps={getRequiredProps()}
            resetNum={resetNum}
            taskObj={taskObj}
            getTaskIdResult={getTaskIdResult}
            setDialogOpen={setDialogOpen}
            setResetNum={setResetNum}
          />
          <ProvisionConfirm
            requiredProps={getRequiredProps()}
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            rowData={rowData}
            timezone={timezone}
            region={region}
            imageVersion={imageVersion}
            adomVersion={adomVersion}
            provisionInstance={provisionInstance}
            pdf={serviceTermPdf}
            server={server}
          ></ProvisionConfirm>
        </Col>
      </Row>
    </Grid>
  );
};
interface IProps {
  imageVersion: string;
  setImageVersion: any;
  setAdomVersion: any;
  setTimezone: any;
  region: Record<string, any>;
  timezone: Record<string, any>;
  setRegion: any;
  resetNum: number;
}
const ProvisionForm = ({
  region,
  setRegion,
  resetNum,
  imageVersion,
  setImageVersion
}: IProps) => {
  const { t } = useTranslation();
  const [showImageVersion, setShowImageVersion] = useState<boolean>(true);
  const setSelectedRegion = (selectedItem?: Record<string, any>) => {
    setShowImageVersion(selectedItem?.enableImageVersion);
    !selectedItem?.enableImageVersion && setImageVersion('');
    setRegion(selectedItem);
  };

  const setSelectedImageVersion = (selectedValue?: string) => {
    setImageVersion(selectedValue || '');
  };

  useEffect(() => {
    !showImageVersion && setImageVersion('');
  }, [showImageVersion]);
  return (
    <div>
      <Stack>
        <Form
          style={{
            marginLeft: '50px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: 'auto',
            gap: '0px 60px'
          }}
        >
          <Form.Group
            controlId="region"
            className="provision-form-group"
            style={{
              marginBottom: '10px'
            }}
          >
            <Form.ControlLabel>
              {t('common.provision.region')}
            </Form.ControlLabel>
            <div className={'rs-form-control-wrapper'}>
              <RegionSelector
                onSelectHandle={setSelectedRegion}
                resetNum={resetNum}
                server={server}
              />
            </div>
          </Form.Group>
          <Form.Group
            controlId="image-version"
            className="provision-form-group"
            style={{
              marginBottom: '10px',
              display: `${
                showImageVersion && region?.rawValue ? 'flex' : 'none'
              }`
            }}
          >
            <Form.ControlLabel>
              {t('common.provision.imageVersion')}
            </Form.ControlLabel>
            <div className={'rs-form-control-wrapper'}>
              <ImageVersionSelector
                region={region?.rawValue}
                resetNum={resetNum}
                onSelectHandle={setSelectedImageVersion}
              />
            </div>
          </Form.Group>
        </Form>
      </Stack>
    </div>
  );
};

export default ProvisionService;
